import React from "react";
import { IResource } from "@gettactic/api";
import { MapCanvasValue } from "@gettactic/map-builder/src/components/map/contexts/MapCanvasContext";
import { MapBuilderViewer } from "@gettactic/map-builder/src/MapBuilderViewer";
import { ReactSVG } from "react-svg";
import { useBooleanState } from "@gettactic/helpers";
import { useMap } from "@webV2/map/MapProvider";
import { MapLoadingSpinner } from "@webV2/map/MapView/MapLoadingSpinner";
import { useSvgToPngImage } from "@gettactic/helpers/src/hooks/useSvgToPngImage";

export interface FloorMapProps {
  floor: IResource;
  isEditing?: boolean;
  onMapLoaded?: (error: Error | null, svg?: SVGSVGElement) => void;
  onMapEditLoaded?: (error: Error | null, svg?: SVGSVGElement) => void;
  style?: React.CSSProperties;
  showLabel?: boolean;
  label?: string;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  renderAsImage?: boolean;
  loadSvgAsImage?: boolean;
}

export const FloorMap: React.FC<FloorMapProps> = ({
  floor,
  isEditing = false,
  onMapLoaded,
  onMapEditLoaded,
  style,
  showLabel = false,
  label = "",
  onMouseLeave,
  onMouseEnter,
  renderAsImage,
  loadSvgAsImage = false
}) => {
  const { forceMapBuilder } = useMap();
  const svgElRef = React.useRef<SVGSVGElement | null>(null);
  const mapData = floor.map_data
    ? (JSON.parse(floor.map_data) as MapCanvasValue)
    : undefined;

  const isMapLoaded = useBooleanState();

  const { imgSrc, status, error } = useSvgToPngImage({
    svg: svgElRef.current,
    scale: 2,
    enabled: renderAsImage && !!svgElRef.current && isMapLoaded.state,
    debug: true // set to true for verbose console logs
  });

  const isMapBuilder = forceMapBuilder || (!floor.map_url && !!mapData);

  const handleSvgReady = (errorArg: Error | null, svg?: SVGSVGElement) => {
    if (errorArg) {
      if (isEditing) onMapEditLoaded?.(errorArg, svg);
      else onMapLoaded?.(errorArg, svg);
      return;
    }
    if (!svg) return;
    svgElRef.current = svg;

    if (!renderAsImage) {
      if (isEditing) onMapEditLoaded?.(null, svg);
      else onMapLoaded?.(null, svg);
    }

    isMapLoaded.true();
    // When renderAsImage=true, wait for <img onLoad>
  };

  const handleImageLoad = () => {
    if (!svgElRef.current) return;
    if (isEditing) onMapEditLoaded?.(null, svgElRef.current);
    else onMapLoaded?.(null, svgElRef.current);
  };

  const handleSvgImageLoad = () => {
    isMapLoaded.true();
    onMapLoaded?.(null, undefined);
  };

  const commonStyle: React.CSSProperties = {
    display: "block",
    borderRadius: 8,
    cursor: "default",
    outline: "none",
    ...(style || {})
  };

  return (
    <>
      {loadSvgAsImage && floor.map_url ? (
        <img
          src={`${floor.map_url}?t=svg`}
          alt={label || floor.name || "map"}
          style={commonStyle}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
          onLoad={handleSvgImageLoad}
          onError={handleSvgImageLoad}
          draggable={false}
        />
      ) : imgSrc && renderAsImage ? (
        <img
          src={imgSrc}
          alt={label || floor.name || "map"}
          style={commonStyle}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
          onLoad={handleImageLoad}
          draggable={false}
        />
      ) : isMapBuilder ? (
        <div
          style={{
            display: "block",
            borderRadius: "8px",
            cursor: "default",
            outline: "none"
          }}
        >
          <div>
            <MapBuilderViewer
              floor={floor || undefined}
              resources={[]}
              interactiveZoneInfo={{}}
              highlightResource={undefined}
              mapEditPosition={null}
              ref={(svg) => {
                //if (isMapLoaded.state) return;
                if (!svg) return;

                if (isEditing) {
                  onMapEditLoaded?.(null, svg);
                  return;
                }
                onMapLoaded?.(null, svg);
                isMapLoaded.true();
              }}
              style={style}
              useSvg
            />
          </div>
        </div>
      ) : (
        <ReactSVG
          afterInjection={(err, svg) => {
            handleSvgReady(err || null, svg || undefined);
          }}
          src={`${floor.map_url}?t=svg`}
          loading={MapLoadingSpinner}
          style={style}
          key={`${floor.id}-${isEditing ? "edit" : "view"}`}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
        />
      )}
      {/* LABEL */}
      {isMapLoaded.state && showLabel && (
        <div
          className={"pointer-events-none flex items-center justify-center"}
          style={{
            transform: "rotateX(0deg)",
            transformStyle: "preserve-3d",
            pointerEvents: "none"
          }}
        >
          <div
            className={
              "pointer-events-none mt-2 inline-block rounded-md border-1 border-neutral-400 bg-secondary px-2 py-1 text-center font-medium text-4xl text-neutral-800 shadow-lg"
            }
            style={{
              transform: "rotateX(0deg)",
              transformStyle: "preserve-3d"
            }}
          >
            {label || floor.name || ""}
          </div>
        </div>
      )}
    </>
  );
};
